'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useMemo } from 'react';
import { Dropdown, DropdownOption, c } from 'ui-components';
import { SortControlProps } from './Sort.types';

/**
 * Presentational sort dropdown — the caller supplies the engine-backed hook
 * output.
 */
export const SortControl = ({
  currentSortValue,
  sortCriteria,
  onSort,
  className = '',
  disabled = false,
}: SortControlProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  const sortByLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.sort-by',
    'Sort by'
  );

  const options: DropdownOption[] = useMemo(
    () =>
      sortCriteria.map((opt) => ({
        label: getMicrocopyValue(
          searchLabelsMicrocopy,
          `search.${opt.value}`,
          opt.value.charAt(0).toUpperCase() + opt.value.slice(1)
        ),
        value: opt.value,
      })),
    [searchLabelsMicrocopy, sortCriteria]
  );

  const baseOption =
    options.find((opt) => opt.value === currentSortValue) ?? options[0];

  const selectedOption: DropdownOption = useMemo(
    () => ({
      ...baseOption,
      label: (
        <>
          <span className="text-content-subtle">{sortByLabel}:</span>{' '}
          <span className="font-normal text-content opacity-90">
            {baseOption.label}
          </span>
        </>
      ),
    }),
    [baseOption, sortByLabel]
  );

  return (
    <div className={c('min-w-[180px]', className)}>
      <Dropdown
        id="search-sort"
        options={options}
        selectedOption={selectedOption}
        onSelect={(option) => onSort(option.value)}
        isDarkTheme={false}
        buttonClassName="bg-transparent"
        // No colon: the accessible name stays `Sort by X`, the name the
        // control has always announced.
        ariaLabel={`${sortByLabel} ${baseOption.label}`}
        disabled={disabled}
      />
    </div>
  );
};
